import { createElement, lazy, useMemo } from 'react';
import { Navigate, RouterProvider, createBrowserRouter, useLocation, type RouteObject } from 'react-router-dom';
import { useAuth } from '@/core/auth/AuthProvider';
import { ADMIN_ENTRY_PERMISSIONS, ADVERTISER_ROLES, RequirePermission } from '@/core/auth/permissions';
import { destinationQuery } from '@/core/auth/destination';
import { useConfig } from '@/core/config/ConfigProvider';
import { enabledModules } from './module-registry';
import { PortalLayout } from './layouts/PortalLayout';

const HomePage = lazy(() => import('@/modules/home/HomePage'));
const CmsPage = lazy(() => import('@/modules/home/CmsPage'));
const NotFoundPage = lazy(() => import('@/modules/home/NotFoundPage'));

/**
 * ROTEADOR
 *
 * Montado em tempo de execucao a partir de tres fontes:
 *  1. modulos registrados, filtrados pelas feature flags;
 *  2. paginas criadas no CMS (`site.pages`), que existem so no banco;
 *  3. os padroes de URL de SEO do Cap. 27.1, tambem configuraveis.
 *
 * Consequencia pratica: publicar `/lotes/minas-gerais/itauna` como landing
 * page e trabalho de administrador, nao de deploy.
 */

export function RequireRole({ roles, children }: { roles: string[]; children: React.ReactNode }) {
  const { user, status } = useAuth();
  const location = useLocation();

  if (status === 'loading') return null;
  if (!user) return <Navigate to="/entrar" state={{ from: location.pathname }} replace />;
  if (roles.length > 0 && !roles.some((role) => user.roles.includes(role))) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}

/**
 * ANUNCIAR EXIGE CONTA — e quem não tem é levado a CRIAR uma, não a entrar.
 *
 * A diferença entre mandar para `/cadastrar` e mandar para `/entrar` decide o
 * fim da história: quem clica em "Anunciar imóvel" pela primeira vez não tem
 * senha para digitar, e uma tela de login pedindo credencial que ele não
 * possui é onde a pessoa desiste. A tela de cadastro leva para o login em um
 * clique; o contrário custa a conta.
 *
 * Antes, `/anunciar` era público e o cadastro "aconteceria no fim". Não
 * acontecia: o visitante preenchia OITO ETAPAS — fotos incluídas — e recebia
 * 401 ao publicar, com tudo preenchido e nenhum caminho à frente.
 *
 * O destino viaja para que a pessoa volte exatamente para cá depois de criar
 * a conta. Ver `core/auth/destination`.
 */
export function RequireAccount({ roles, children }: { roles: string[]; children: React.ReactNode }) {
  const { user, status } = useAuth();
  const location = useLocation();

  if (status === 'loading') return null;

  if (!user) {
    const destino = `${location.pathname}${location.search}`;
    return <Navigate to={`/cadastrar${destinationQuery(destino)}`} replace />;
  }

  // Conta interna sem papel de anunciante (moderação, financeiro) não
  // publica anúncio — e não é caso de criar outra conta.
  if (roles.length > 0 && !roles.some((role) => user.roles.includes(role))) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

export function AppRouter() {
  const config = useConfig();

  const router = useMemo(() => {
    const isFeatureOn = (key: string) => {
      const flag = config.site.features.find((candidate) => candidate.key === key);
      return Boolean(flag?.enabled);
    };

    const modules = enabledModules(isFeatureOn);

    const publicRoutes: RouteObject[] = modules.flatMap((module) => module.routes ?? []).map((route) =>
      // O módulo declara a rota; quem exige conta é o roteador, num lugar só,
      // para a regra não depender de cada módulo lembrar dela.
      route.path === 'anunciar'
        ? { ...route, element: <RequireAccount roles={ADVERTISER_ROLES}>{route.element}</RequireAccount> }
        : route,
    );

    const advertiserRoutes: RouteObject[] = modules.flatMap((module) => module.advertiserRoutes ?? []);
    const adminRoutes: RouteObject[] = modules.flatMap((module) => module.adminRoutes ?? []);
    const adminLayout = modules.find((module) => module.adminLayout)?.adminLayout;

    // Paginas do CMS. Vem depois das rotas de modulo para que um slug criado
    // no painel nunca sequestre uma rota de produto.
    const cmsRoutes: RouteObject[] = config.site.pages
      .filter((page) => page.publication.enabled)
      .map((page) => ({ path: page.slug, element: <CmsPage slug={page.slug} /> }));

    const routes: RouteObject[] = [
      {
        path: '/',
        element: <PortalLayout />,
        children: [
          { index: true, element: <HomePage /> },
          ...publicRoutes,
          ...cmsRoutes,
          {
            path: 'painel',
            children: advertiserRoutes.length
              ? advertiserRoutes.map((route) => ({
                  ...route,
                  element: <RequireRole roles={ADVERTISER_ROLES}>{route.element}</RequireRole>,
                }))
              : [],
          },
          /*
            O painel tem duas camadas de verificação, e são perguntas
            diferentes:

            1. aqui — "esta pessoa é da equipe interna?"; quem não for nem
               carrega o layout do painel;
            2. em cada rota — "esta pessoa pode ESTA tela?", declarado no
               módulo por permissão.

            As duas são apresentação. A terceira camada, a que importa, é o
            servidor recusando a requisição.
          */
          {
            path: 'admin',
            element: adminLayout ? (
              <RequirePermission permission={ADMIN_ENTRY_PERMISSIONS}>{createElement(adminLayout)}</RequirePermission>
            ) : null,
            children: adminRoutes,
          },
          { path: '*', element: <NotFoundPage /> },
        ],
      },
    ];

    return createBrowserRouter(routes);
  }, [config]);

  return <RouterProvider router={router} />;
}
